import React, { useCallback, useEffect, useState } from 'react';
import { datasetsApi, projectsApi } from '../lib/api';
import { Clock, FolderPlus, Upload } from 'lucide-react';
import { formatDate } from '../lib/utils';

function RecentActivity({ organizationId }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadActivities = useCallback(async () => {
    try {
      const [projectsRes, datasetsRes] = await Promise.all([
        projectsApi.list(organizationId),
        datasetsApi.list({ page: 1, page_size: 10 })
      ]);

      // Combine and sort by date
      const combined = [
        ...projectsRes.data.map(p => ({
          id: p.id,
          type: 'project',
          title: p.name,
          description: 'Project created',
          date: p.created_at,
          icon: FolderPlus
        })),
        ...datasetsRes.data.datasets.map(d => ({
          id: d.id,
          type: 'dataset',
          title: d.name,
          description: `${d.method_type} dataset uploaded`,
          date: d.created_at,
          icon: Upload
        }))
      ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

      setActivities(combined);
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (organizationId) {
      loadActivities();
    }
  }, [organizationId, loadActivities]);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-3">
              <div className="w-8 h-8 bg-muted rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-semibold">Recent Activity</h3>
      </div>

      {activities.length === 0 ? (
        <p className="text-sm text-muted-foreground">No recent activity</p>
      ) : (
        <div className="space-y-4">
          {activities.map(activity => {
            const Icon = activity.icon;
            return (
              <div key={activity.id} className="flex gap-3 items-start">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{activity.title}</p>
                  <p className="text-xs text-muted-foreground">{activity.description}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(activity.date)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default RecentActivity;
